import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { Automation } from "@/api/generated/Automation"
import type { InvokeResult } from "@/api/generated/InvokeResult"
import type { Param } from "@/api/generated/Param"
import { useApiMutation } from "@/api/mutation"

export const PARAM_TYPES = ["string", "number", "file"] as const
export type ParamType = (typeof PARAM_TYPES)[number]

export const automationKeys = {
  all: ["automations"] as const,
  list: () => [...automationKeys.all, "list"] as const,
  detail: (id: number) => [...automationKeys.all, "detail", id] as const,
}

export const automationsQuery = () =>
  queryOptions({
    queryKey: automationKeys.list(),
    queryFn: ({ signal }) => api.get<ListResponse<Automation>>("/automations", { signal }),
    select: (r) => r.items,
  })

export const useAutomations = () => useQuery(automationsQuery())

export interface AutomationInput {
  name?: string
  description?: string
  agent_id?: number
  parameter_schema?: Param[]
  additional_instructions_template?: string
  initial_message_template?: string
  api_invocation_enabled?: boolean
}

function setDetail(qc: ReturnType<typeof useQueryClient>, a: Automation) {
  // never keep the one-time secret in the cache
  const { invocation_secret: _, ...rest } = a
  qc.setQueryData(automationKeys.detail(a.id), rest)
}

export function useCreateAutomation() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<Automation, AutomationInput>({
    mutationFn: (body) => api.post("/automations", body),
    onSuccess: (a) => setDetail(qc, a),
    invalidates: [automationKeys.list()],
    successToast: (a) => t("chat.automations.created", { name: a.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdateAutomation() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<Automation, { id: number } & AutomationInput>({
    mutationFn: ({ id, ...body }) => api.patch(`/automations/${id}`, body),
    onSuccess: (a) => setDetail(qc, a),
    invalidates: [automationKeys.list()],
    successToast: (a) => t("chat.automations.saved", { name: a.name }),
    errorToast: "unlessFields",
  })
}

export function useDeleteAutomation() {
  const { t } = useTranslation()
  return useApiMutation<void, Automation>({
    mutationFn: (a) => api.delete(`/automations/${a.id}`),
    invalidates: [automationKeys.all],
    successToast: (_d, a) => t("chat.automations.deleted", { name: a.name }),
  })
}

export function useRotateAutomationSecret() {
  const qc = useQueryClient()
  return useApiMutation<Automation, number>({
    mutationFn: (id) => api.post(`/automations/${id}/rotate-secret`),
    onSuccess: (a) => setDetail(qc, a),
    invalidates: [automationKeys.list()],
  })
}

/** Runs an automation: JSON for plain values, multipart when files are attached. */
export function useInvokeAutomation() {
  return useApiMutation<
    InvokeResult,
    { id: number; values: Record<string, string>; files: Record<string, File> }
  >({
    mutationFn: ({ id, values, files }) => {
      if (Object.keys(files).length === 0) return api.post(`/automations/${id}/invoke`, values)
      const fd = new FormData()
      for (const [k, v] of Object.entries(values)) fd.append(k, v)
      for (const [k, f] of Object.entries(files)) fd.append(k, f, f.name)
      return api.post(`/automations/${id}/invoke`, fd)
    },
    invalidates: [automationKeys.list()],
    errorToast: "unlessFields",
  })
}
